import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Users } from "@ensafely/checker-ui";
import { setToken } from "../lib/authToken";

type InviteInfo = {
  tenant_name: string;
  role: string;
  inviter_name: string;
  email: string;
  needs_password: boolean;
};

type Status = "loading" | "info" | "submitting" | "success" | "error";

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState("");
  const [info, setInfo] = useState<InviteInfo | null>(null);

  // Form fields for new users
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("No invite token found in URL.");
      return;
    }

    fetch(`/api/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (res.ok) {
          setInfo(body as InviteInfo);
          setStatus("info");
        } else {
          setStatus("error");
          setError(body.error ?? "Invalid invite link.");
        }
      })
      .catch(() => {
        setStatus("error");
        setError("Network error. Please try again.");
      });
  }, [token]);

  const handleAccept = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");

    if (info?.needs_password) {
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }

    setStatus("submitting");

    try {
      const body: Record<string, string> = { token };
      if (info?.needs_password) {
        if (name) body.name = name;
        body.password = password;
      }

      const res = await fetch("/api/auth/accept-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        // Store JWT and redirect to dashboard
        if (data.token) {
          setToken(data.token);
        }
        setStatus("success");
        setTimeout(() => navigate("/"), 1500);
      } else {
        setStatus("info");
        setError(data.error ?? "Failed to accept invite.");
      }
    } catch {
      setStatus("info");
      setError("Network error. Please try again.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md bg-card rounded-lg shadow p-8">
          <h1 className="text-2xl font-bold mb-4">Invalid invite link</h1>
          <p className="text-sm text-muted-foreground mb-4">
            This invite link is missing a token. Please check your email for the
            correct link.
          </p>
          <Link to="/login" className="text-primary hover:underline text-sm">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md bg-card rounded-lg shadow p-8">
        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p>Loading invite details...</p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-3 text-center">
            <XCircle className="h-12 w-12 text-destructive" />
            <h1 className="text-2xl font-bold">Invite Error</h1>
            <p className="text-base text-destructive">{error}</p>
            <p className="text-sm text-muted-foreground mt-2">
              The invite may have expired or already been used. Ask your team
              admin to send a new invite.
            </p>
            <Link
              to="/login"
              className="mt-4 inline-block px-6 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            >
              Go to Login
            </Link>
          </div>
        )}

        {status === "success" && (
          <div className="flex flex-col items-center gap-3 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            <h1 className="text-2xl font-bold">Welcome aboard!</h1>
            <p className="text-sm text-muted-foreground">
              You've joined <strong>{info?.tenant_name}</strong>. Redirecting to
              dashboard...
            </p>
          </div>
        )}

        {(status === "info" || status === "submitting") && info && (
          <>
            <div className="flex flex-col items-center gap-2 mb-6 text-center">
              <Users className="h-10 w-10 text-primary" />
              <h1 className="text-2xl font-bold">Team Invitation</h1>
              <p className="text-sm text-muted-foreground">
                <strong>{info.inviter_name}</strong> has invited you to join{" "}
                <strong>{info.tenant_name}</strong> as{" "}
                <span className="capitalize">{info.role}</span>.
              </p>
            </div>

            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

            {info.needs_password ? (
              <form onSubmit={handleAccept} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Set up your account to get started.
                </p>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                />
                <input
                  type="password"
                  placeholder="Password (min. 8 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                />
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                />
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full bg-primary text-primary-foreground rounded py-2 text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {status === "submitting" ? "Setting up..." : "Set password & join"}
                </button>
              </form>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  You already have an account ({info.email}). Click below to join
                  the team.
                </p>
                <button
                  onClick={() => handleAccept()}
                  disabled={status === "submitting"}
                  className="w-full bg-primary text-primary-foreground rounded py-2 text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {status === "submitting" ? "Joining..." : "Accept invitation"}
                </button>
              </div>
            )}

            <p className="text-sm text-muted-foreground mt-4 text-center">
              <Link to="/login" className="text-primary hover:underline">
                Back to login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
